import { hasRenderableData } from './formDataUtils';
import type { ChatResponse, DocumentChatResponse } from '../types/chat';

const EMPTY_EXTRACTION_FAILURE_MESSAGE = /couldn'?t extract structured data/i;
const PARTIAL_EXTRACTION_FALLBACK_MESSAGE = 'Here is the information extracted so far.';

export function isEmptyExtractionFailureMessage(text: string | undefined | null): boolean {
  return typeof text === 'string' && EMPTY_EXTRACTION_FAILURE_MESSAGE.test(text);
}

export function hasRenderableExtractedData(data: unknown): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }
  return hasRenderableData({ extractedData: data });
}

function resolveResponseAfterPromotion(
  currentResponse: string | undefined,
  fallbackMessage: string = PARTIAL_EXTRACTION_FALLBACK_MESSAGE,
): string {
  if (currentResponse?.trim() && !isEmptyExtractionFailureMessage(currentResponse)) {
    return currentResponse;
  }
  return fallbackMessage;
}

/**
 * Prefer streamed/incremental fields when the final payload is empty/unusable.
 * Shared by ChatService stream completion and Chat.tsx processing-state promotion.
 */
export function promoteIncrementalExtractIntoResult<T extends ChatResponse | DocumentChatResponse>(
  result: T,
  incrementalExtract: Record<string, unknown> | null | undefined,
): T {
  if (!incrementalExtract || !hasRenderableExtractedData(incrementalExtract)) {
    return result;
  }
  if (hasRenderableExtractedData(result.extractedData)) {
    return result;
  }
  if (hasRenderableExtractedData(result.rawExtractedData)) {
    return {
      ...result,
      extractedData: result.rawExtractedData,
      showFormWithData: true,
      is_form_response: true,
    };
  }

  return {
    ...result,
    response: resolveResponseAfterPromotion(result.response),
    extractedData: incrementalExtract,
    rawExtractedData: result.rawExtractedData ?? incrementalExtract,
    showFormWithData: true,
    is_form_response: true,
    formMappingApplied: result.formMappingApplied ?? true,
    status: result.status ?? 'partial',
    is_partial: true,
  };
}
